import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
// import { isNull } from 'lodash';

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TIMES = [
    '12:00',
    '12:30',
    '01:00',
    '01:30',
    '02:00',
    '02:30',
    '03:00',
    '03:30',
    '04:00',
    '04:30',
    '05:00',
    '05:30',
    '06:00',
    '06:30',
    '07:00',
    '07:30',
    '08:00',
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
];

const DmeDateTimePicker = ({
    dateFormat,
    className,
    onChange,
    selected = null,
    selectPastDates = false,
    selectWeekends = false,
    selectTime = true
}) => {
    const [dateTime, setDateTime] = useState(null);
    const [showWidget, setShowWidget] = useState(false);

    const [year, setYear] = useState();
    const [month, setMonth] = useState(); // 0-January, 11-December
    // const [date, setDate] = useState();

    const refInputWidget = useRef();
    const refDateTimePickerWidget = useRef();
    let _dateFormat = null;
    if (selectTime) {
        _dateFormat = dateFormat || 'DD-MM-YYYY hh:mm A';
    } else {
        _dateFormat = dateFormat || 'DD-MM-YYYY';
    }
    const isAm = dateTime ? dateTime.hour() < 12 : null;

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutsidePicker);
        setDateTime(selected);
        refInputWidget.current.value = selected
            ? moment(selected).format(_dateFormat)
            : '';
        return () => {
            document.removeEventListener('mousedown', handleClickOutsidePicker);
        };
    }, []);

    useEffect(() => {
        if (selected) {
            setDateTime(moment(selected));
            setYear(selected.getFullYear());
            setMonth(selected.getMonth());
        } else {
            const now = moment();
            setYear(now.year());
            setMonth(now.month());
        }
        refInputWidget.current.value = selected
            ? moment(selected).format(_dateFormat)
            : '';
    }, [selected ? selected.getTime() : null]);

    const triggerOnClick = (_dateTime) => {
        refInputWidget.current.value = _dateTime
            ? _dateTime.format(_dateFormat)
            : '';
        if (moment.isMoment(_dateTime)) {
            onChange(_dateTime.toDate());
        } else {
            onChange(null);
        }
    };

    const handleClickOutsidePicker = (event) => {
        if (
            !refInputWidget.current.contains(event.target) &&
            !refDateTimePickerWidget.current.contains(event.target)
        ) {
            setShowWidget(false);
            reviseDateTime();
        }
    };

    const reviseDateTime = () => {
        const _inputedDateTime = moment(
            refInputWidget.current.value,
            _dateFormat
        ); // 'true' enforces strict parsing
        if (
            _inputedDateTime.isValid() &&
            !(
                !selectPastDates &&
                _inputedDateTime.isBefore(moment().startOf('day'))
            ) && !(isWeekend(_inputedDateTime) && !selectWeekends)
        ) {
            setDateTime(_inputedDateTime);
            triggerOnClick(_inputedDateTime);
        } else {
            refInputWidget.current.value = dateTime
                ? dateTime.format(_dateFormat)
                : '';
        }
    };

    const handleClear = () => {
        setDateTime(null);
        triggerOnClick(null);
    };

    // const handleApply = () => {
    //     setShowWidget(false);
    // };

    const handleClickDate = (_date) => {
        setDateTime((prev) => {
            if (moment.isMoment(prev)) {
                const newDateTime = moment({
                    year,
                    month,
                    date: _date,
                    hour: prev.hour(),
                    minute: prev.minute(),
                });
                triggerOnClick(newDateTime);
                return newDateTime;
            } else {
                const newDateTime = moment({
                    year,
                    month,
                    date: _date,
                    hour: 0,
                    minute: 0,
                });
                triggerOnClick(newDateTime);
                return newDateTime;
            }
        });
    };

    const handleClickAmPm = (amPm) => {
        if (dateTime === null) {
            const now = moment();
            const newDateTime = moment({
                year: now.year(),
                month: now.month(),
                date: now.date(),
                hour: amPm === 'am' ? 0 : 12,
                minute: 0,
            });
            triggerOnClick(newDateTime);
            return;
        }
        if (isAm && amPm === 'am') return;
        if (isAm === false && amPm === 'pm') return;
        const newDateTime = moment({
            year: dateTime.year(),
            month: dateTime.month(),
            date: dateTime.date(),
            hour:
                amPm === 'am'
                    ? dateTime.hour() % 12
                    : (dateTime.hour() % 12) + 12,
            minute: dateTime.minute(),
        });
        setDateTime(newDateTime);
        triggerOnClick(newDateTime);
    };

    const handleClickTime = (timeIdx) => {
        const _time = moment({ hour: isAm === false ? 12 : 0, minute: 0 }).add(
            30 * timeIdx,
            'minute'
        );
        setDateTime((prev) => {
            if (moment.isMoment(prev)) {
                const newDateTime = moment({
                    year: prev.year(),
                    month: prev.month(),
                    date: prev.date(),
                    hour: _time.hour(),
                    minute: _time.minute(),
                });
                triggerOnClick(newDateTime);
                return newDateTime;
            } else {
                const now = moment();
                const newDateTime = moment({
                    year: now.year(),
                    month: now.month(),
                    date: now.date(),
                    hour: _time.hour(),
                    minute: _time.minute(),
                });
                triggerOnClick(newDateTime);
                return newDateTime;
            }
        });
    };

    const nextMonth = () => {
        const _tmp = moment().set({ year, month }).add(1, 'month');
        const newYear = _tmp.year();
        const newMonth = _tmp.month();
        setYear(newYear);
        setMonth(newMonth);
    };

    const prevMonth = () => {
        const _tmp = moment().set({ year, month }).subtract(1, 'month');
        const prevYear = _tmp.year();
        const prevMonth = _tmp.month();
        setYear(prevYear);
        setMonth(prevMonth);
    };

    const isWeekend = (date) => {
        const day = moment(date).day(); // 0 = Sunday, 6 = Saturday
        return day === 0 || day === 6;
    };

    const getCalendarBodyContent = () => {
        const monthStartDay = moment({ year, month }).startOf('month').day();
        const numberOfCurrentMonthDates = moment()
            .set({ year, month, date: 1 })
            .daysInMonth();
        const number_of_last_month_dates = moment()
            .set({ year, month, date: 1 })
            .subtract(1, 'days')
            .date();
        let ddDelta = -monthStartDay + 2;
        if (ddDelta > 0) ddDelta -= 7;
        const now = moment();
        return [...Array(6).keys()].map((row) => {
            return (
                <div
                    className='calendar-row'
                    key={row}
                >
                    {[...Array(7).keys()].map((_day) => {
                        let dd = ddDelta + row * 7 + _day;
                        if (dd <= 0) {
                            return (
                                <button
                                    type='button'
                                    className='disabled'
                                    key={dd}
                                    onClick={() => prevMonth()}
                                >
                                    {dd + number_of_last_month_dates}
                                </button>
                            );
                        } else if (dd <= numberOfCurrentMonthDates) {
                            let isSelected = false;
                            let isToday = false;
                            let isPastDate = false;
                            if (
                                dateTime &&
                                dateTime.year() === year &&
                                dateTime.month() === month &&
                                dateTime.date() === dd
                            ) {
                                isSelected = true;
                            }
                            if (
                                now.year() === year &&
                                now.month() === month &&
                                now.date() === dd
                            ) {
                                isToday = true;
                            }
                            if (
                                moment([year, month, dd]).isBefore(
                                    now.startOf('day')
                                )
                            ) {
                                isPastDate = true;
                            }
                            let isDisabled = (isWeekend(moment([year, month, dd])) && !selectWeekends) || (isPastDate && !selectPastDates);
                            return (
                                <button
                                    type='button'
                                    className={
                                        (isToday ? 'today ' : '') +
                                        (isSelected ? ' active ' : '') +
                                        (isDisabled
                                            ? ' disabled'
                                            : '')
                                    }
                                    key={dd}
                                    onClick={
                                        isDisabled
                                            ? null
                                            : () => handleClickDate(dd)
                                    }
                                >
                                    {dd}
                                </button>
                            );
                        } else {
                            return (
                                <button
                                    type='button'
                                    className='disabled'
                                    key={dd}
                                    onClick={() => nextMonth()}
                                >
                                    {dd - numberOfCurrentMonthDates}
                                </button>
                            );
                        }
                    })}
                </div>
            );
        });
    };

    const renderCalendar = () => {
        return (
            <div className='calendar-container'>
                <div className='month-year-button-container'>
                    <div
                        className='prev'
                        onClick={() => prevMonth()}
                    >
                        <i className='fa fa-chevron-left' />
                    </div>
                    <select
                        className='month'
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    >
                        {MONTHS.map((each, idx) => (
                            <option
                                key={each}
                                value={idx}
                            >
                                {each}
                            </option>
                        ))}
                    </select>
                    <select
                        className='year'
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {[...Array(5).keys()].map((_, idx) => {
                            return (
                                <option
                                    key={idx}
                                    value={year + idx - 2}
                                >
                                    {year + idx - 2}
                                </option>
                            );
                        })}
                    </select>
                    <div
                        className='next'
                        onClick={() => nextMonth()}
                    >
                        <i className='fa fa-chevron-right' />
                    </div>
                </div>
                <div className='calendar-widget'>
                    <div className='calendar-header'>
                        {DAYS.map((day) => (
                            <div key={day}>{day}</div>
                        ))}
                    </div>
                    <div className='calendar-body'>
                        {getCalendarBodyContent()}
                    </div>
                </div>
            </div>
        );
    };

    const renderTimePicker = () => {
        return (
            <div className='time-picker-container'>
                <div className='am-pm'>
                    <button
                        type='button'
                        className={isAm === true ? 'active' : ''}
                        onClick={() => handleClickAmPm('am')}
                    >
                        AM
                    </button>
                    <button
                        type='button'
                        className={isAm === false ? 'active' : ''}
                        onClick={() => handleClickAmPm('pm')}
                    >
                        PM
                    </button>
                </div>
                <div className='time-list'>
                    {[...Array(6).keys()].map((row) => (
                        <div
                            className='time-list-row'
                            key={row}
                        >
                            {[...Array(4).keys()].map((col) => {
                                let selected = false;
                                if (
                                    moment.isMoment(dateTime) &&
                                    dateTime.format('hh:mm A') ===
                                        TIMES[row * 4 + col] +
                                            (isAm ? ' AM' : ' PM')
                                ) {
                                    selected = true;
                                }
                                return (
                                    <button
                                        type='button'
                                        className={selected ? 'active' : ''}
                                        key={row * 4 + col}
                                        onClick={() =>
                                            handleClickTime(row * 4 + col)
                                        }
                                    >
                                        {TIMES[row * 4 + col]}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderButtonWidget = () => {
        return (
            <div className='buttons-container'>
                <button
                    className='clear'
                    type='button'
                    onClick={() => handleClear()}
                >
                    Clear
                </button>
                <button
                    type='button'
                    onClick={() => setShowWidget(false)}
                >
                    Ok
                </button>
            </div>
        );
    };

    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            reviseDateTime();
        }
    };

    return (
        <React.Fragment>
            <div className='dme-date-time-picker'>
                <input
                    className={className}
                    // value={dateTime ? dateTime.format(_dateFormat) : ''}
                    ref={refInputWidget}
                    onFocus={() => setShowWidget(true)}
                    onKeyDown={handleInputKeyDown}
                    onBlur={() => reviseDateTime()}
                />
                <div className='dme-date-time-picker-widget-container'>
                    <div
                        className={
                            'dme-date-time-picker-widget ' +
                            (showWidget ? '' : 'hidden')
                        }
                        ref={refDateTimePickerWidget}
                    >
                        {renderCalendar()}
                        { selectTime ? renderTimePicker() : '' }
                        {renderButtonWidget()}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

DmeDateTimePicker.propTypes = {
    onChange: PropTypes.func,
    className: PropTypes.string,
    dateFormat: PropTypes.string,
    selected: PropTypes.instanceOf(Date),
    selectPastDates: PropTypes.bool,
    selectWeekends: PropTypes.bool,
    selectTime: PropTypes.bool
};

DmeDateTimePicker.defaultProps = {
    onChange: () => {}, // Default empty function if onChange is not passed
    className: '',
    dateFormat: '',
    selected: null,
    selectPastDates: false,
    selectWeekends: false
};

export default DmeDateTimePicker;



// WEBPACK FOOTER //
// ./src/components/DmeDateTimePicker.js